import { useState, useEffect } from "react";
import api from "@/lib/api";
import { PageHeader, Panel, Loading, StatusBadge } from "@/components/common";
import { ChartLineUp, CurrencyDollar, Cpu, Buildings, Robot } from "@phosphor-icons/react";

const money = (n) => `£${Number(n || 0).toFixed(2)}`;

export default function CostDashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/ai/cost-dashboard").then(({ data }) => setD(data)).catch(() => setErr(true)); }, []);

  if (err) return <div className="p-6"><PageHeader icon={ChartLineUp} title="AI Cost Dashboard" subtitle="You need financial permission to view company AI spend." /></div>;
  if (!d) return <Loading />;
  const maxDay = Math.max(...d.series.map((s) => s.cost), 0.0001);

  return (
    <div>
      <PageHeader icon={ChartLineUp} title="AI Cost Dashboard"
        subtitle="Real AI spend across every studio, provider and agent — measured from the usage log, not estimated."
        badge={<StatusBadge status="LIVE" />} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Total AI spend" value={money(d.total_est_cost)} icon={CurrencyDollar} color="#00E5FF" />
        <Stat label="AI calls" value={d.count} icon={Robot} color="#00FF9D" />
        <Stat label="Spent today" value={money(d.budget?.spent_today)} icon={CurrencyDollar} color="#FFB800" />
        <Stat label="This month" value={money(d.budget?.spent_month)} icon={CurrencyDollar} color="#FF007F" />
      </div>

      <Panel className="mb-6">
        <div className="flex items-center gap-2 mb-4"><ChartLineUp size={18} weight="fill" className="text-[#00E5FF]" /><span className="font-display font-bold text-white">Spend — last 14 days</span></div>
        <div className="flex items-end gap-1.5 h-40" data-testid="cost-series">
          {d.series.map((s) => (
            <div key={s.date} className="flex-1 flex flex-col items-center justify-end group">
              <div className="w-full rounded-t bg-gradient-to-t from-[#00E5FF]/30 to-[#00E5FF] transition-all"
                style={{ height: `${Math.max(2, (s.cost / maxDay) * 100)}%` }} title={`${s.date}: ${money(s.cost)}`} />
              <span className="text-[9px] text-zinc-600 mt-1 rotate-45 origin-left whitespace-nowrap">{s.date.slice(5)}</span>
            </div>
          ))}
        </div>
      </Panel>

      <div className="grid md:grid-cols-3 gap-4">
        <Breakdown title="By Provider" icon={Cpu} rows={d.by_provider} total={d.total_est_cost} testid="by-provider" />
        <Breakdown title="By Agent" icon={Robot} rows={d.by_agent} total={d.total_est_cost} testid="by-agent" />
        <Breakdown title="By Studio" icon={Buildings} rows={d.by_studio} total={d.total_est_cost} testid="by-studio" />
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color }) {
  return (
    <Panel className="flex items-center gap-3 py-3">
      <Icon size={22} weight="duotone" style={{ color }} />
      <div><div className="font-display text-xl font-extrabold text-white leading-none">{value}</div><div className="text-[11px] text-zinc-500 mt-0.5">{label}</div></div>
    </Panel>
  );
}

function Breakdown({ title, icon: Icon, rows, total, testid }) {
  return (
    <Panel data-testid={testid}>
      <div className="flex items-center gap-2 mb-3"><Icon size={16} className="text-zinc-400" /><span className="font-medium text-white">{title}</span></div>
      {(!rows || rows.length === 0) ? <p className="text-xs text-zinc-500">No spend yet.</p> : (
        <div className="space-y-2.5">
          {rows.map((r) => (
            <div key={r.name}>
              <div className="flex justify-between text-xs mb-1"><span className="text-zinc-300 capitalize truncate">{r.name}</span><span className="text-zinc-400">£{Number(r.cost).toFixed(2)}</span></div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-[#00E5FF]" style={{ width: `${total ? (r.cost / total) * 100 : 0}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
